import { useQuery } from "@tanstack/react-query";

import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getBinaryResponse } from "~/shared/services/api/fetch-client.ts";

export async function fetchDocumentByPoAndFileName(
	poNumber: number,
	fileName: string,
	isInternal = false,
	signal?: AbortSignal,
): Promise<ArrayBuffer | null> {
	// A regenerated document can be re-uploaded under the same filename, so the browser's
	// HTTP cache must be bypassed on every call, not just React Query's.
	const requestOptions = {
		cache: "no-store" as const,
		...(signal ? { signal } : {}),
	};
	const baseUrl = isInternal
		? apiRoutes.GETSHIPMENTDOCUMENTBYNAME
		: apiRoutes.GETINVOICEDOCUMENTBYNAME;
	const url = baseUrl
		.replace(":poNumber", poNumber.toString())
		.replace(":fileName", encodeURIComponent(fileName));

	try {
		const resp = await getBinaryResponse(url, requestOptions);

		if (resp.success) {
			return resp.data;
		} else {
			console.error("Failed to fetch invoice documents:", String(resp.error));
			return null;
		}
	} catch (error) {
		console.error(
			"Failed to fetch invoice documents:",
			error instanceof Error ? error.message : String(error),
		);
		return null;
	}
}

export const useGetDocumentByPoAndFileName = (
	poNumber: number | undefined,
	fileName: string | undefined,
	enabled = true,
	isInternal = false,
) => {
	const { data, isLoading, error, refetch } = useQuery({
		queryKey: ["selectedInvoiceDocument", poNumber, fileName, isInternal],
		queryFn: async ({ signal }) => {
			return await fetchDocumentByPoAndFileName(
				poNumber ?? 0,
				fileName ?? "",
				isInternal,
				signal,
			);
		},
		enabled: !!poNumber && !!fileName && enabled,
		staleTime: 0,
		gcTime: 0,
	});
	return {
		document: data,
		isLoading,
		error,
		refetch,
	};
};
